import React, { useEffect, useState } from "react";
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { auth } from "../firebase";
import {
  registerPushToken,
  shouldOfferNotificationPrompt,
} from "../services/notifications";

const DISMISS_KEY = "notifications_prompt_dismissed_v1";

// Web persists dismissal in localStorage (like PWAInstallPrompt). Native has no
// synchronous storage here, so it relies on a module flag for the session plus
// the OS permission gate (shouldOfferNotificationPrompt returns false once the
// user has answered the system dialog), which prevents re-nagging after launch.
let nativeDismissedThisSession = false;

const isDismissed = (): boolean => {
  if (Platform.OS === "web") {
    try {
      return window.localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  }
  return nativeDismissedThisSession;
};

const markDismissed = (): void => {
  if (Platform.OS === "web") {
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore storage failures
    }
    return;
  }
  nativeDismissedThisSession = true;
};

/**
 * One-time in-app prompt asking the user to turn on push notifications on this
 * device. Rendered inside the authenticated tree (see App.tsx). Shows only when
 * a user is signed in and the platform hasn't been asked yet.
 */
export default function EnableNotificationsPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (isDismissed()) return;
    if (!auth.currentUser?.uid) return;

    shouldOfferNotificationPrompt().then((offer) => {
      if (!cancelled && offer) setVisible(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleNo = () => {
    markDismissed();
    setVisible(false);
  };

  const handleYes = async () => {
    markDismissed();
    setVisible(false);
    const uid = auth.currentUser?.uid;
    // registerPushToken resolves per platform — native requests the OS
    // permission and registers the device token; web runs the browser flow.
    if (uid) await registerPushToken(uid);
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleNo}
    >
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Text style={styles.title}>Enable notifications</Text>
          <Text style={styles.body}>
            Get notified when a new conversation starts or someone joins yours.
            Enable notifications on this device?
          </Text>
          <View style={styles.actions}>
            <Pressable
              onPress={handleNo}
              style={styles.buttonGhost}
              testID="notifications-prompt-no"
            >
              <Text style={styles.buttonGhostText}>Not now</Text>
            </Pressable>
            <Pressable
              onPress={handleYes}
              style={styles.buttonPrimary}
              testID="notifications-prompt-yes"
            >
              <Text style={styles.buttonPrimaryText}>Enable</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  card: {
    width: "100%",
    maxWidth: 360,
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 24,
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    color: "#232335",
    marginBottom: 8,
  },
  body: {
    fontSize: 15,
    lineHeight: 21,
    color: "#4A4A57",
    marginBottom: 20,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
  },
  buttonGhost: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  buttonGhostText: {
    fontSize: 15,
    fontWeight: "600",
    color: "#6B6B78",
  },
  buttonPrimary: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: "#232335",
  },
  buttonPrimaryText: {
    fontSize: 15,
    fontWeight: "600",
    color: "#fff",
  },
});
